<template>
  <teleport to="#prompt-overlay">
    <transition @before-leave="updateStatusBar($route.query.backFromCard)" @before-enter="updateStatusBar(true)" @after-leave="$emit('close')">
      <div class="h-full w-full top-0 left-0 fixed flex items-center justify-center" v-if="show">
        <div :class="[
          'h-full w-full top-0 left-0 absolute bg-black/75 zeit-background',
          isMobile ? 'z-20' : '',
        ]" @click="show = false || !enableClose" />
        <div
            :class="[
              'zeit-card bg-zinc-100 dark:bg-zinc-900 z-20 max-h-full',
              isMobile ? 'absolute -bottom-24 left-0 right-0 pt-10 rounded-t-3xl' : '',
              !isMobile ? (paddingY + ' px-2 rounded-xl') : '',
              scrollPosition == 0 ? 'transition-enabled' : '',
              cardClasses,
              maxWidth
            ]"
            :style="{transform: 'translateY(' + scrollPosition + 'px)'}"
            @touchstart="handleTouchStart($event)"
            @touchmove="handleTouchMove($event)"
            @touchend="handleTouchEnd($event)"
        >

          <div class="w-full flex justify-center" v-if="isMobile"><div class="absolute w-12 h-1 rounded-full bg-black/20 dark:bg-white/20 top-4" /></div>
          <div :class="['px-4 py-2 text-lg font-semibold', $slots.default ? 'pb-2' : '']" v-if="$slots.title"><slot name="title" /></div>
          <div :class="[
            'h-full px-4 pb-4 overflow-auto overflow-x-hidden',
            isMobile ? 'safe-wrap-b' : '',
            dragStartPosition ? 'overscroll-none' : '',
          ]" style="max-height: 95vh;" ref="body">
            <slot />
            <div class="w-full flex justify-center mt-0" v-if="!isMobile && desktopCloseLabel && enableClose">
              <zeit-button @click="show = false" color="zinc" fill="clear" class="mx-2">{{ desktopCloseLabel }}</zeit-button>
              <slot name="additionalButtons" />
            </div>
          </div>
          <slot name="footer" />
        </div>
      </div>
    </transition>
  </teleport>
</template>

<style scoped>
  div.safe-wrap-b {
    padding-bottom: calc(env(safe-area-inset-bottom) + 7.5rem);
  }

  .v-enter-active,
  .v-leave-active,
  .zeit-background,
  .transition-enabled.zeit-card {
    transition: all .3s ease;
  }

  .v-enter-from .zeit-background,
  .v-leave-to .zeit-background {
    opacity: 0;
  }

  .v-enter-from .zeit-card,
  .v-leave-to .zeit-card {
    opacity: 0;
    transform: translateY(100%) !important;
  }
</style>

<script lang="ts">
  import { ref, defineComponent } from 'vue'

  import { lightImpact } from '@/globals/haptics';
  import { Style } from '@capacitor/status-bar';
  import { updateIosBar } from '@/globals/statusbar';

  import ZeitButton from '@/components/ui/v2/ZeitButton.vue';

  // Treat any viewport narrower than tailwind's sm breakpoint as mobile so the
  // prompt slides up as a bottom sheet, not a centered modal. Without this we
  // depend on the build-time `VITE_APP_IS_MOBILE` env var, which is only set
  // for native iOS / Android builds — so a LAN preview opened on a phone falls
  // back to the desktop modal and the surrounding page shifts under it.
  const MOBILE_MAX_WIDTH = 640;

  export default defineComponent({
    components: { ZeitButton },
    inject: { injectedIsMobile: { from: 'isMobile', default: false } },
    props: {
      desktopCloseLabel: String,
      enableClose: {
        type: Boolean,
        default: true,
      },
      cardClasses: Array,
      maxWidth: {
        type: String,
        default: 'max-w-lg',
      },
      paddingY: {
        type: String,
        default: 'py-6',
      },
    },
    data() {
      return {
        dragStartPosition: undefined,
        scrollPosition: 0,
        innerScrollPosition: 0,
        viewportIsMobile: typeof window !== 'undefined' && window.innerWidth < MOBILE_MAX_WIDTH,
      }
    },
    computed: {
      isMobile(): boolean {
        return Boolean(this.injectedIsMobile) || this.viewportIsMobile;
      },
    },
    emits: ['close'],
    methods: {
      getPosition(e) {
        return e.layerY || e.touches[0].clientY;
      },
      handleTouchStart(e) {
        this.dragStartPosition = this.getPosition(e);
      },
      handleTouchMove(e) {
        if (this.$refs.body.scrollTop > 0) {
          this.scrollPosition = 0;
          this.dragStartPosition = undefined;
          return
        }
        if (!this.dragStartPosition) return;

        let minPosition = -20;
        if (this.$refs.body.offsetHeight < this.$refs.body.scrollHeight) {
          minPosition = 0;
        }

        this.scrollPosition = Math.max(this.getPosition(e) - this.dragStartPosition, minPosition);
      },
      handleTouchEnd(e) {
        if (!this.dragStartPosition) return;
        if (this.scrollPosition >= 40) this.show = false;
        this.scrollPosition = 0;
      },
      updateStatusBar(isCard: boolean) {
        if (isCard) {
          updateIosBar({}, Style.Dark);
        } else {
          updateIosBar({});
        }
      },
    },
    setup() {
      const show = ref(false)

      return {
        show,
      }
    },
    mounted() {
      this.show = true;
      lightImpact();
    },
  })
</script>
